/* oxlint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';
import prom from 'promjs-plus';
import { axios, Url } from '@rossum/api-client';
import { isDevFeatureEnabled } from '../constants/dev';
import { useFeatureFlag } from '../unleash/useFeatureFlag';
import { getNetworkQuality } from './measureLatency';
import { sendMetricsToPrometheus } from './sendMetricsToPrometheus';

const registry = prom();

type MetricName = 'frontend_document_list_load_time_seconds';

type MetricsState =
  | { status: 'waiting' }
  | { status: 'started'; startTime: number }
  | { status: 'completed'; startTime: number; endTime: number };

const usePrometheusMetrics = (
  timerCondition: boolean,
  metricsMetaData: {
    metricName: MetricName;
    description: string;
  },
  enabled: boolean,
  organizationUrl?: Url
) => {
  const [metrics, setMetrics] = useState<MetricsState>({ status: 'waiting' });
  const ignoreMeasurement = useRef<boolean>(false);

  const isHypercareEnabled = useFeatureFlag('hypercare-metrics');

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 429) {
          ignoreMeasurement.current = true;
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const startTimer = useCallback(() => {
    if (metrics.status === 'waiting') {
      setMetrics({ status: 'started', startTime: performance.now() });
    }
  }, [metrics.status]);

  const endTimer = useCallback(() => {
    if (metrics.status === 'started') {
      setMetrics({
        status: 'completed',
        startTime: metrics.startTime,
        endTime: performance.now(),
      });
    }
  }, [metrics]);

  useEffect(() => {
    if (enabled) {
      if (!timerCondition) {
        startTimer();
      } else {
        endTimer();
      }
    }
  }, [enabled, endTimer, metrics.status, startTimer, timerCondition]);

  useEffect(() => {
    if (
      metrics.status === 'completed' &&
      enabled &&
      ignoreMeasurement.current === false
    ) {
      const { metricName, description } = metricsMetaData;

      const bucket =
        metricName === 'frontend_document_list_load_time_seconds'
          ? [0.2, 0.5, 1, 1.5, 2.5, 3, 6, 8, 10, 13, 18]
          : [1, 2, 3, 5, 8, 10, 11, 13, 15, 18, 21];

      const histogram = registry.create(
        'histogram',
        metricName,
        description,
        bucket
      );

      const time = (metrics.endTime - metrics.startTime) / 1000;

      const shouldHaveLabel = organizationUrl && isHypercareEnabled;
      const networkQuality = getNetworkQuality();

      histogram.observe(time, {
        ...(shouldHaveLabel ? { organizationUrl } : undefined),
        ...(networkQuality !== 'unknown' ? { networkQuality } : undefined),
      });

      if (isDevFeatureEnabled('console-log')) {
        console.log(
          `Sending metrics to prom ${metricsMetaData.metricName}: ${time}`
        );
      }

      sendMetricsToPrometheus(registry.metrics());
      histogram.resetAll();
      registry.clear();

      setMetrics({ status: 'waiting' });
    }
  }, [metrics, metricsMetaData, enabled, organizationUrl, isHypercareEnabled]);
};

export default usePrometheusMetrics;
